import { Box } from '@mui/material';

import type { Interval } from 'components/roster-portal/activity-planning/hooks/useTimelineInterval';

import Appointments from './Appointments';
import TimelineTooltipContainer from './TimelineTooltipContainer';
import usePlanningEntity from '../../hooks/usePlanningEntity';
import { ZoomLevel } from '../TimelineBar';
import { getItemWidthInPixels } from '../utils';
import { EditAppointmentType } from 'RestAPIs/activities/types';
import { useNavParams } from 'hooks';
import { View } from '../../enums';
import { PlanningEntity } from '../../types';
import styled, { keyframes } from 'styled-components';
import { rgbaOrange } from 'constants/colors';

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 1px 0 ${rgbaOrange(0.5)};
  }
  50% {
    box-shadow: 0 0 8px 0 ${rgbaOrange(0.8)};
  }
  100% {
    box-shadow: 0 0 1px 0 ${rgbaOrange(0.5)};
  }
`;

const fadeOutOutline = keyframes`
  0% {
    outline-color: ${rgbaOrange(0.8)};
  }
  100% {
    outline-color: transparent;
  }
`;

// Styled component for the highlight effect
export const HighlightedRoute = styled(Box)`
  &.highlighted {
    outline: 1px solid ${rgbaOrange(1)};
    border-radius: 8px;
    animation: 
      ${pulseAnimation} 3s ease-in-out, 
      ${fadeOutOutline} 0.5s ease-in-out 3s forwards; /* Delayed fade-out */
    }
  }
`;

interface Props {
  activeEntityContainingSelection: string | null;
  onChangeMousePosition?: (pos: { x: number; y: number }) => void;
  onChangeActiveEntity?: (entityId: string) => void;
  onChangeSelection?: (selectionIds: string[]) => void;
  timelineRouteWidth: number;
  timeBarItemWidth: number;
  positionOffset: number;
  interval: Interval;
  planningEntity: PlanningEntity;
  zoomLevel: ZoomLevel;
  connectAppointmentGrouping: (
    appointmentGroupingEntityId: string,
    editAppointmentType?: EditAppointmentType,
    scheduleStart?: string
  ) => void;
}

export default function TimelineRoute(props: Props) {
  const {
    activeEntityContainingSelection,
    connectAppointmentGrouping,
    positionOffset,
    interval,
    onChangeMousePosition,
    onChangeActiveEntity,
    onChangeSelection,
    planningEntity,
    zoomLevel
  } = props;
  const { planningEntityIdentifier, schedules } = planningEntity;
  const { start } = schedules?.[0] || {};

  const { params } = useNavParams();
  const view = params?.view as View;

  const [planningEntityTypePreference] = usePlanningEntity();
  const planningEntityType = planningEntityTypePreference[view];

  const widthInPixels = getItemWidthInPixels(
    props.timeBarItemWidth,
    interval.durationInSeconds,
    zoomLevel
  );

  return (
    <HighlightedRoute
      sx={{
        position: 'relative',
        width: widthInPixels
      }}
      className={`__${planningEntity?.id}__`}
    >
      <TimelineTooltipContainer
        planningEntity={planningEntity}
        scheduleStart={start}
        start={interval.start}
        positionOffset={positionOffset}
        planningEntityIdentifier={planningEntityIdentifier}
        zoomLevel={zoomLevel}
        timeBarItemWidth={props.timeBarItemWidth}
      >
        <Appointments
          activeEntityContainingSelection={activeEntityContainingSelection}
          connectAppointmentGrouping={connectAppointmentGrouping}
          planningEntity={planningEntity}
          planningEntityType={planningEntityType}
          interval={interval}
          zoomLevel={zoomLevel}
          timeBarItemWidth={props.timeBarItemWidth}
          onChangeMousePosition={onChangeMousePosition}
          onChangeActiveEntity={onChangeActiveEntity}
          onChangeSelection={onChangeSelection}
        />
      </TimelineTooltipContainer>
    </HighlightedRoute>
  );
}
